import React, { useEffect } from "react";
import { RouteComponentProps, navigate } from "@reach/router";
import { useFetcher } from "rest-hooks";
import { ApplicationsResource, LoginResource } from "../../resources";
import {
	CardActionArea,
	Grid,
	makeStyles,
	Typography,
	Icon,
} from "@material-ui/core";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { FormattedMessage, useIntl } from "react-intl";
import clsx from "clsx";
import { useCommonWidths } from "../../hooks";
import { MyPagePaper, MyPagePanel, SnackbarContentWrapper } from "../common";
import { useSnackbar } from "notistack";
import FetchDployScript from "../helpers/FetchDployScript";
import DployTokenResource from "../../resources/DployTokenResource";

interface IApplicationsProps extends RouteComponentProps {
	allApps: ApplicationsResource[];
}

interface ApplicationListItemProps {
	application: ApplicationsResource;
	dploy: any;
}

const useStyles = makeStyles((theme) => ({
	imageIconWrapper: {
		height: 196,
		[theme.breakpoints.down("sm")]: {
			height: 180,
		},
		[theme.breakpoints.down("xs")]: {
			height: 112,
		},
	},
	objectImage: {
		margin: "auto",
		display: "block",
		maxWidth: "100%",
		maxHeight: "100%",
		objectFit: "cover",
		width: "100%",
		height: "-webkit-fill-available",
		borderRadius: 8,
	},
	objectIcon: {
		margin: "auto",
		width: "100%",
		height: "100%",
		textAlign: "center",
		fontSize: "10rem",
		marginTop: 8,
		[theme.breakpoints.down("sm")]: {
			fontSize: "8rem",
			marginTop: 24,
		},
		[theme.breakpoints.down("xs")]: {
			fontSize: "4rem",
			marginTop: 24,
		},
	},
	statusApproved: {
		color: "green",
	},
	statusManualUW: {
		color: "orange",
	},
	statusDeclined: {
		color: "red",
	},
	boldText: {
		fontWeight: "bold",
	},
	singleRowWrapper: {
		display: "flex",
		alignItems: "baseline",
	},
	singleRowExtraValue: {
		marginLeft: 8,
	},
	truncateText: {
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	listItem: {
		margin: 20,
		[theme.breakpoints.down("sm")]: {
			margin: 0,
			marginTop: 20,
			marginBottom: 20,
		},
	},
	cardPadding: {
		padding: 8,
	},
}));

const Applications = (props: IApplicationsProps) => {
	const { allApps } = props;

	const loginStatus = useFetcher(LoginResource.statusShape());
	useEffect(() => {
		async function fetchLoginStatus() {
			const { isAuthenticated } = await loginStatus({});
		}
		fetchLoginStatus();
	}, [loginStatus]);

	return (
		<MyPagePanel>
			<Typography variant="h4" gutterBottom align="center">
				<FormattedMessage
					id="minside.applicationlist.choose_application"
					defaultMessage="Velg søknad"
					description="Information text to choose applicaiton from list"
				/>
			</Typography>
			<Typography variant="body2" gutterBottom align="center">
				<FormattedMessage
					id="minside.applicationlist.description"
					defaultMessage={
						allApps.length > 0 ?
							"Velg søknaden du vil fullføre. Husk at du må fullføre søknaden innen fristen for at tilbudet skal være gjeldende." :
							"Du har ingen lånesøknader til behandling hos oss nå."
						}
					description="Information text above application list"
				/>
			</Typography>

			<FetchDployScript>
				{(dploy: any) =>
					allApps.map((a) => (
						<ApplicationListItem
							key={a.applicationId}
							application={a}
							dploy={dploy}
						/>
					))
				}
			</FetchDployScript>
		</MyPagePanel>
	);
};

interface ApplicationTokenProps {
	lockToApplicationToken: string;
}

const checkToken = (data: ApplicationTokenProps) => {
	if (!data || !data.lockToApplicationToken) throw Error("invalid token");
	return data.lockToApplicationToken;
};

const ApplicationListItem = (props: ApplicationListItemProps) => {
	const { application, dploy } = props;
	const fetchLockToken = useFetcher(DployTokenResource.currentShape());
	const { enqueueSnackbar } = useSnackbar();
	const intl = useIntl();

	const classes = useStyles();

	const isMobile = useCommonWidths();
	const { isBetweenXsAndSm, isXsDown } = isMobile;

	const getStatusColor = () => {
		const statusId = application.applicationStatusId as number;
		if ([5, 6, 14, 23, 26].includes(statusId)) return classes.statusApproved;
		if ([4, 17, 24].includes(statusId)) return classes.statusManualUW;
		if ([7, 8, 9].includes(statusId)) return classes.statusDeclined;
	};

	const openApplication = () => {
		const newWindowTab = window.open("");
		newWindowTab?.document.write("Vennligst vent");

		fetchLockToken({ applicationNumber: application.applicationNumber }, {})
			.then(checkToken)
			.then((token) => {
				dploy.init({ token: token }).then((instanceName: string) => {
					dploy.loadApplication({
						appNo: application.applicationNumber,
						container: newWindowTab,
					});
				});
			})
			.catch((err) => {
				newWindowTab?.close();

				if (err.toString().includes("Unauthorized")) {
					enqueueSnackbar("", {
						key: application.applicationNumber,
						content: (key: any) => (
							<SnackbarContentWrapper
								variant="warning"
								message={
									<FormattedMessage
										id="minside.invoicestabpage.unauth_message"
										defaultMessage="Sesjonen har utløpt. Vennligst logg inn på nytt"
										description="Session expired"
									/>
								}
								id={key}
							/>
						),
					});
					navigate("/logout", { state: { errorCode: 401 } });
				} else {
					enqueueSnackbar("", {
						key: application.applicationNumber,
						content: (key) => (
							<SnackbarContentWrapper
								variant="error"
								message={
									<FormattedMessage
										id="minside.applicationlist.error_message"
										defaultMessage="En feil har oppstått"
										description="Error message in applicationlist"
									/>
								}
								id={key}
							/>
						),
					});
				}
			});
	};

	return (
		<MyPagePaper noRoot className={classes.listItem}>
			<CardActionArea className={classes.cardPadding} onClick={openApplication}>
				<Grid
					container
					direction="row"
					spacing={isXsDown ? 1 : isBetweenXsAndSm ? 2 : 4}
				>
					<Grid item xs={4} className={classes.imageIconWrapper}>
						{application.imageUrl ? (
							<img
								className={classes.objectImage}
								src={`${application.imageUrl}`}
								alt="Objektet du ønsker å kjøpe"
							/>
						) : (
							<Icon
								className={clsx(
									`fas fa-${application.objectIcon}`,
									classes.objectIcon
								)}
								color="primary"
								fontSize="large"
							/>
						)}
					</Grid>
					{application.objectYear && (
						<div
							style={{
								color: "#fff",
								backgroundColor: "rgba(0,0,0,0.5)",
								padding: 8,
								position: "absolute",
								top: 12,
								left: 12,
								borderRadius: 8,
							}}
						>
							{application.objectYear}
						</div>
					)}
					<Grid
						item
						xs={8}
						container
						direction="row"
						style={{ alignItems: "center", alignContent: "center" }}
					>
						<Grid
							item
							xs={isXsDown ? 12 : 6}
							className={classes.singleRowWrapper}
						>
							<Typography
								variant={isXsDown ? "body1" : "h6"}
								className={classes.boldText}
							>
								{application.productName}
							</Typography>
							{isXsDown ? (
								<Typography className={classes.singleRowExtraValue}>
									<FormattedMessage
										id="minside.applications.objectPrice"
										defaultMessage="{objectPrice}"
										description="Object price"
										values={{
											objectPrice: Intl.NumberFormat(intl.locale, {
												style: "currency",
												currency: "NOK",
												maximumFractionDigits: 0,
												minimumFractionDigits: 0,
											}).format(application.objectPrice || 0),
										}}
									/>
								</Typography>
							) : null}
						</Grid>

						<Grid item xs={isXsDown ? 12 : 6}>
							<Typography className={getStatusColor()}>
								{application.applicationStatus}
							</Typography>
						</Grid>
						<Grid item xs={isXsDown ? 12 : 6}>
							<Typography
								variant={isXsDown ? "body1" : isBetweenXsAndSm ? "h6" : "h5"}
								className={clsx(classes.truncateText, {
									[classes.boldText]: application.objectMake,
								})}
							>
								{application.objectMake ? (
									<FormattedMessage
										id="minside.applications.make_model"
										defaultMessage="{objectMake} {objectModel}"
										description="Make model"
										values={{ ...application }}
									/>
								) : (
									<FormattedMessage
										id="minside.applicationlist.missing_object"
										defaultMessage="Ubestemt kjøretøy"
										description="Defaultmessage for missing Object in Applicationlist"
									/>
								)}
							</Typography>
						</Grid>
						<Grid
							item
							xs={isXsDown ? 12 : 6}
							style={{ display: "flex", alignItems: "center" }}
						>
							<Typography
								variant={isXsDown ? "body1" : isBetweenXsAndSm ? "h6" : "h5"}
								color="primary"
							>
								{application.isDueCompletion ? (
									<FormattedMessage
										id="minside.applicationlist.finish_application"
										defaultMessage="Fullfør søknad"
										description="Finish application text"
									/>
								) : application.isEsigned ? (
									<FormattedMessage
										id="minside.applicationlist.enter_application"
										defaultMessage="Se søknad/ Last opp dokumenter"
										description="Enter application display page"
									/>
								) : (
									<FormattedMessage
										id="minside.applicationlist.edit_application"
										defaultMessage="Endre søknad"
										description="Edit application text"
									/>
								)}
							</Typography>
							<ChevronRightIcon color="primary" />
						</Grid>
						{!isXsDown && (
							<Grid container style={{ alignItems: "center" }}>
								<Grid item xs={6}>
									<Typography variant="h6" className={classes.boldText}>
										<FormattedMessage
											id="minside.applicationlist.edit_application.objectprice"
											defaultMessage="{objectPrice}"
											description="Object price"
											values={{
												objectPrice: Intl.NumberFormat(intl.locale, {
													style: "currency",
													currency: "NOK",
													maximumFractionDigits: 0,
													minimumFractionDigits: 0,
												}).format(application.objectPrice || 0),
											}}
										/>
									</Typography>
								</Grid>
								<Grid item xs={6}>
									<Typography>
										<FormattedMessage
											id="minside.applications.app_no"
											defaultMessage="{applicationNumber}"
											description="App number"
											values={{
												applicationNumber: application.applicationNumber,
											}}
										/>
									</Typography>
								</Grid>
							</Grid>
						)}
					</Grid>
				</Grid>
			</CardActionArea>
		</MyPagePaper>
	);
};

export default Applications;
